import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, FileCode, FileText, Link2, MessageSquare, Presentation, Image as ImageIcon } from "lucide-react";
import Sidebar from "@/pages/home/components/Sidebar";
import { getKnowledgeItemById } from "@/pages/knowledge/itemStore";

type ArxivMeta = {
  title?: string;
  abstract?: string;
  authors?: string[];
  published?: string;
};

function parseArxivId(input: string): string | null {
  const v = input.trim();
  if (!v) return null;
  // abs/pdf URL
  const m1 = v.match(/arxiv\.org\/abs\/([^?#]+)/i);
  if (m1?.[1]) return m1[1].replace(/\.pdf$/i, "");
  const m2 = v.match(/arxiv\.org\/pdf\/([^?#]+)/i);
  if (m2?.[1]) return m2[1].replace(/\.pdf$/i, "");
  // raw id formats (new / old)
  if (/^\d{4}\.\d{4,5}(v\d+)?$/i.test(v)) return v;
  if (/^[a-z-]+\/\d{7}(v\d+)?$/i.test(v)) return v;
  return null;
}

async function tryFetchArxivMeta(arxivId: string): Promise<ArxivMeta | null> {
  try {
    const url = `https://export.arxiv.org/api/query?id_list=${encodeURIComponent(arxivId)}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const xml = await res.text();
    const doc = new DOMParser().parseFromString(xml, "application/xml");
    const entry = doc.querySelector("entry");
    if (!entry) return null;
    const title = entry.querySelector("title")?.textContent?.replace(/\s+/g, " ").trim();
    const abstract = entry.querySelector("summary")?.textContent?.replace(/\s+/g, " ").trim();
    const authors = Array.from(entry.querySelectorAll("author > name"))
      .map((n) => n.textContent?.trim())
      .filter(Boolean) as string[];
    const published = entry.querySelector("published")?.textContent?.slice(0, 10);
    return { title, abstract, authors, published };
  } catch {
    return null;
  }
}

function mockPaper(arxivId: string, fallbackTitle: string) {
  const year = `20${arxivId.slice(0, 2)}`;
  const title =
    fallbackTitle.startsWith("arXiv：") || fallbackTitle === arxivId
      ? `A Practical Study on ${arxivId} (arXiv Preprint)`
      : fallbackTitle;
  const authors = ["A. Researcher", "B. Scientist", "C. Engineer"];
  const abstract =
    "本文针对一个典型研究问题给出方法设计、实验设置与关键结论。我们将核心贡献拆分为可复现的模块，并在多个基线条件下进行对照实验，展示性能提升与局限性。最后给出潜在应用场景与未来工作方向，便于快速上手与进一步扩展。";
  const bullets = [
    "核心贡献：提出可复现的改进模块，降低实现门槛。",
    "实验结论：在主要指标上稳定提升，且对参数较鲁棒。",
    "局限性：对数据分布变化敏感，建议补充跨域验证。",
  ];
  return { year, title, authors, abstract, bullets };
}

function mockPdfPreview(seedTitle: string) {
  const pages = [
    { title: "摘要与关键结论", bullets: ["研究目标与背景概述", "核心贡献与创新点", "主要实验结果与结论"] },
    { title: "方法与设置", bullets: ["数据来源与口径", "实验设计与对照", "指标与评估方法"] },
    { title: "结果与讨论", bullets: ["关键图表与对比", "误差与消融分析", "局限性与风险点"] },
    { title: "附录与复现", bullets: ["环境依赖与脚本", "参数表与随机种子", "复现步骤 checklist"] },
  ];
  return { pages, seedTitle };
}

function mockDocxPreview(seedTitle: string) {
  const sections = [
    { h: "1. 背景与问题定义", p: "本文档总结了该主题的背景、现状与核心问题，并给出研究范围与边界条件。" },
    { h: "2. 方法与流程", p: "给出完整流程与关键步骤，包含输入/输出、假设与注意事项，便于落地执行。" },
    { h: "3. 结论与建议", p: "给出可执行建议清单与风险提示，建议优先验证 2~3 个关键假设点。" },
    { h: "4. 附录（资料/引用）", p: "列出引用条目、相关链接与补充材料，便于后续扩展与溯源。" },
  ];
  return { sections, seedTitle };
}

function mockCodePreview() {
  const code = [
    "def evaluate(records, metric='f1'):",
    "    scores = []",
    "    for r in records:",
    "        s = run_model(r['input'])",
    "        scores.append(score(s, r['label'], metric=metric))",
    "    return sum(scores) / max(1, len(scores))",
    "",
    "# usage",
    "avg = evaluate(dataset, metric='f1')",
    "print(f'avg={avg:.4f}')",
  ].join("\n");
  return { lang: "python", code };
}

function mockSocialPreview(url: string) {
  return {
    url,
    title: "网页内容导入（演示）",
    desc: "已抓取页面标题、摘要与关键段落，后续可继续抽取概念并关联到知识库索引图谱。",
    highlights: ["关键观点 1：定义与结论", "关键观点 2：方法与证据", "关键观点 3：实践建议与风险"],
  };
}

function mockResultPreview() {
  return {
    kpis: [
      { k: "综合评分", v: "0.82", note: "置信区间 0.78–0.86" },
      { k: "主要提升项", v: "+23%", note: "相对基线" },
      { k: "资源开销", v: "+6%", note: "可接受" },
    ],
    plan: ["本周：复现实验并补齐对照", "下周：补齐图表与消融", "月底：输出终稿与归档"],
  };
}

export default function KnowledgeItemPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [pdfOk, setPdfOk] = useState(true);
  const item = itemId ? getKnowledgeItemById(itemId) : null;

  const arxivId = useMemo(() => {
    if (!item) return null;
    return item.arxivId || parseArxivId(item.title) || parseArxivId(item.absUrl || "") || parseArxivId(item.pdfUrl || "") || null;
  }, [item]);

  const absUrl = useMemo(() => {
    if (!item) return "";
    if (item.absUrl) return item.absUrl;
    if (arxivId) return `https://arxiv.org/abs/${arxivId}`;
    return "";
  }, [item, arxivId]);

  const pdfUrl = useMemo(() => {
    if (!item) return "";
    if (item.pdfUrl) return item.pdfUrl;
    if (arxivId) return `https://arxiv.org/pdf/${arxivId}.pdf`;
    return "";
  }, [item, arxivId]);

  const [meta, setMeta] = useState<ArxivMeta | null>(null);
  useEffect(() => {
    if (!arxivId) return;
    let alive = true;
    void tryFetchArxivMeta(arxivId).then((m) => {
      if (!alive) return;
      if (m) setMeta(m);
    });
    return () => {
      alive = false;
    };
  }, [arxivId]);

  if (!item) {
    return (
      <div className="flex h-screen overflow-hidden bg-white text-foreground">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background">
          <div className="border-b border-border bg-background">
            <div className="mx-auto flex h-16 w-full max-w-[1100px] items-center px-5 md:px-6 lg:px-8">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
                返回
              </button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 md:px-6 lg:px-8">
            <div className="mx-auto w-full max-w-[1100px]">
              <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
                未找到该条目（可能已被删除或尚未导入）。请返回知识库重新进入。
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const shownTitle = meta?.title || item.title;
  const shownAbstract = item.abstract || meta?.abstract || "";
  const shownAuthors = item.authors?.length ? item.authors : meta?.authors;
  const shownDate = meta?.published || item.year;
  const mock = arxivId ? mockPaper(arxivId, shownTitle) : null;
  const finalTitle = shownTitle && !shownTitle.startsWith("arXiv：") ? shownTitle : mock?.title || shownTitle;
  const finalAbstract = shownAbstract || mock?.abstract || "";
  const finalAuthors = shownAuthors?.length ? shownAuthors : mock?.authors;
  const finalDate = shownDate || mock?.year;

  const previewKind = item.sourceType;
  const pdfMock = useMemo(() => mockPdfPreview(finalTitle), [finalTitle]);
  const docxMock = useMemo(() => mockDocxPreview(finalTitle), [finalTitle]);
  const codeMock = useMemo(() => mockCodePreview(), []);
  const socialMock = useMemo(() => mockSocialPreview(absUrl || item.absUrl || item.pdfUrl || item.title), [absUrl, item.absUrl, item.pdfUrl, item.title]);
  const resultMock = useMemo(() => mockResultPreview(), []);

  return (
    <div className="flex h-screen overflow-hidden bg-white text-foreground">
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background">
        <div className="border-b border-border bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="mx-auto flex h-16 w-full max-w-[1100px] items-center justify-between gap-4 px-5 md:px-6 lg:px-8">
            <div className="flex min-w-0 items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
                返回
              </button>
              <span className="min-w-0 truncate text-sm font-semibold text-foreground">{finalTitle}</span>
            </div>
            <div className="flex items-center gap-2">
              {absUrl ? (
                <a
                  href={absUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                >
                  <Link2 className="h-4 w-4 text-muted-foreground" />
                  打开摘要页
                  <ExternalLink className="h-4 w-4 text-muted-foreground" />
                </a>
              ) : null}
              {pdfUrl ? (
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:opacity-90"
                >
                  <FileText className="h-4 w-4" />
                  打开PDF
                  <ExternalLink className="h-4 w-4" />
                </a>
              ) : null}
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 md:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[1100px] space-y-5">
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">文献信息</div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-border bg-background p-4">
                  <div className="text-xs font-medium text-muted-foreground">来源</div>
                  <div className="mt-1 text-sm font-semibold text-foreground">{item.sourceType === "arxiv" ? "arXiv" : item.sourceType.toUpperCase()}</div>
                  {arxivId ? <div className="mt-1 text-xs text-muted-foreground">ID：{arxivId}</div> : null}
                </div>
                <div className="rounded-xl border border-border bg-background p-4">
                  <div className="text-xs font-medium text-muted-foreground">时间</div>
                  <div className="mt-1 text-sm font-semibold text-foreground">{finalDate || item.updatedAt.slice(0, 10)}</div>
                  <div className="mt-1 text-xs text-muted-foreground">最近更新：{item.updatedAt}</div>
                </div>
              </div>
              {finalAuthors?.length ? (
                <div className="mt-4 rounded-xl border border-border bg-background p-4">
                  <div className="text-xs font-medium text-muted-foreground">作者</div>
                  <div className="mt-1 text-sm text-foreground">{finalAuthors.slice(0, 8).join(" · ")}{finalAuthors.length > 8 ? " …" : ""}</div>
                </div>
              ) : null}
              {finalAbstract ? (
                <div className="mt-4 rounded-xl border border-border bg-background p-4">
                  <div className="text-xs font-medium text-muted-foreground">摘要</div>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-7 text-foreground">{finalAbstract}</p>
                </div>
              ) : (
                <div className="mt-4 rounded-xl border border-dashed border-border bg-background p-4 text-sm text-muted-foreground">
                  暂无摘要信息。你仍然可以打开 PDF 或摘要页阅读原文。
                </div>
              )}

              {mock?.bullets?.length ? (
                <div className="mt-4 rounded-xl border border-border bg-background p-4">
                  <div className="text-xs font-medium text-muted-foreground">AI 导读（演示）</div>
                  <div className="mt-2 space-y-2 text-sm text-foreground">
                    {mock.bullets.map((b) => (
                      <div key={b} className="flex gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
                        <span className="leading-relaxed">{b}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            {/* Preview by type (visual, not raw code) */}
            {previewKind === "pdf" || previewKind === "arxiv" || previewKind === "doi" || previewKind === "ris" ? (
              <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-foreground">预览</div>
                    <div className="mt-1 text-sm text-muted-foreground">优先展示可视化预览；如有原文链接可直接打开。</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {absUrl ? (
                      <a
                        href={absUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                      >
                        <Link2 className="h-4 w-4 text-muted-foreground" />
                        原文页
                        <ExternalLink className="h-4 w-4 text-muted-foreground" />
                      </a>
                    ) : null}
                    {pdfUrl ? (
                      <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:opacity-90"
                      >
                        <FileText className="h-4 w-4" />
                        打开PDF
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    ) : null}
                  </div>
                </div>

                {pdfUrl ? (
                  pdfOk ? (
                    <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-background">
                      <iframe title="paper-pdf" className="h-[720px] w-full" src={pdfUrl} onError={() => setPdfOk(false)} />
                    </div>
                  ) : (
                    <div className="mt-4 rounded-2xl border border-dashed border-border bg-background p-5 text-sm text-muted-foreground">
                      当前环境不支持嵌入式 PDF 预览，请使用右上角“打开PDF”。
                    </div>
                  )
                ) : (
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    {pdfMock.pages.map((p, idx) => (
                      <div key={p.title} className="rounded-2xl border border-border bg-background p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-xs font-semibold text-foreground">
                            第 {idx + 1} 页 · {p.title}
                          </div>
                          <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-[10px] text-muted-foreground">A4</span>
                        </div>
                        <div className="mt-3 space-y-2 text-xs text-muted-foreground">
                          {p.bullets.map((b) => (
                            <div key={b} className="flex gap-2">
                              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
                              <span className="leading-relaxed">{b}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-muted">
                          <div className="h-full rounded-full bg-primary/70" style={{ width: `${55 + idx * 10}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            {previewKind === "docx" ? (
              <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-foreground">文档预览（演示）</div>
                    <div className="mt-1 text-sm text-muted-foreground">结构化展示章节与摘要，避免原始代码/格式噪音。</div>
                  </div>
                  <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-xs font-medium text-muted-foreground">DOCX</span>
                </div>
                <div className="mt-4 space-y-3">
                  {docxMock.sections.map((s) => (
                    <div key={s.h} className="rounded-2xl border border-border bg-background p-4">
                      <div className="text-sm font-semibold text-foreground">{s.h}</div>
                      <div className="mt-2 text-sm leading-7 text-muted-foreground">{s.p}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {previewKind === "chat" ? (
              <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-foreground">对话预览（演示）</div>
                    <div className="mt-1 text-sm text-muted-foreground">以聊天形式展示沉淀的关键结论与上下文。</div>
                  </div>
                  <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-xs font-medium text-muted-foreground">Chat</span>
                </div>
                <div className="mt-4 space-y-3">
                  {[
                    { r: "user", t: "帮我总结一下这份对话的关键结论？" },
                    { r: "ai", t: "结论：建议以语义色为核心收敛 UI 色彩，减少彩色噪点；图表使用统一淡蓝线条，删除按钮保持 destructive 语义；对话区固定高度并内部滚动。" },
                    { r: "user", t: "有哪些可执行的下一步？" },
                    { r: "ai", t: "下一步：1) 梳理所有页面残留的硬编码颜色；2) 给知识库条目增加可视化预览；3) 引入统一的删除/确认弹窗组件。" },
                  ].map((m, i) => (
                    <div key={i} className={`flex gap-2.5 ${m.r === "user" ? "flex-row-reverse" : ""}`}>
                      <div className={`max-w-[720px] rounded-2xl px-4 py-3 text-sm leading-7 ${m.r === "ai" ? "bg-muted" : "bg-foreground text-background"}`}>
                        {m.t}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {previewKind === "social" ? (
              <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-foreground">链接预览（演示）</div>
                    <div className="mt-1 text-sm text-muted-foreground">网页卡片 + 关键段落提要，便于快速浏览。</div>
                  </div>
                  <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-xs font-medium text-muted-foreground">Link</span>
                </div>
                <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-background">
                  <div className="aspect-[16/7] bg-muted/20" />
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-foreground">{socialMock.title}</div>
                      <a href={socialMock.url} target="_blank" rel="noreferrer" className="text-xs font-medium text-primary hover:underline">
                        打开链接
                      </a>
                    </div>
                    <div className="mt-2 text-sm leading-7 text-muted-foreground">{socialMock.desc}</div>
                    <div className="mt-3 grid gap-2 sm:grid-cols-3">
                      {socialMock.highlights.map((h) => (
                        <div key={h} className="rounded-xl border border-border bg-muted/10 p-3 text-xs text-muted-foreground">
                          {h}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {previewKind === "code" ? (
              <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-foreground">代码预览（演示）</div>
                    <div className="mt-1 text-sm text-muted-foreground">以可读的代码块展示，而不是 HTML 原始源码。</div>
                  </div>
                  <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-xs font-medium text-muted-foreground">Code</span>
                </div>
                <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-background">
                  <div className="flex items-center gap-2 border-b border-border bg-muted/10 px-4 py-2 text-xs text-muted-foreground">
                    <FileCode className="h-4 w-4" />
                    {codeMock.lang} · `eval.py`
                  </div>
                  <pre className="overflow-x-auto p-4 text-xs leading-6 text-foreground">
                    <code>{codeMock.code}</code>
                  </pre>
                </div>
              </div>
            ) : null}

            {previewKind === "result" ? (
              <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-foreground">成果预览（演示）</div>
                    <div className="mt-1 text-sm text-muted-foreground">以指标卡 + 计划拆解的方式呈现成果核心信息。</div>
                  </div>
                  <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-xs font-medium text-muted-foreground">Result</span>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-3">
                  {resultMock.kpis.map((k) => (
                    <div key={k.k} className="rounded-2xl border border-border bg-background p-4">
                      <div className="text-xs font-medium text-muted-foreground">{k.k}</div>
                      <div className="mt-1 text-xl font-semibold text-foreground">{k.v}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{k.note}</div>
                      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-primary/70" style={{ width: `${Math.min(95, 60 + k.v.length * 6)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 rounded-2xl border border-border bg-background p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <Presentation className="h-4 w-4 text-muted-foreground" />
                    下一步计划
                  </div>
                  <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                    {resultMock.plan.map((p) => (
                      <div key={p} className="flex gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
                        <span className="leading-relaxed">{p}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
}

